import { Tooltip } from 'radix-ui';
import {
  createContext,
  createElement,
  useContext,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
} from 'react';

/**
 * Texts the components need themselves (screen reader labels, empty lists). @nexus/ui has no
 * i18n of its own: the app passes translated labels in, so every text exists in German and
 * English (CLAUDE.md, section UI).
 */
export interface UiLabels {
  close: string;
  loading: string;
  moreActions: string;
  noResults: string;
  required: string;
  optional: string;
  clear: string;
  selectAll: string;
  selectRow: string;
  sortAscending: string;
  sortDescending: string;
  previousPage: string;
  nextPage: string;
  retry: string;
  confirm: string;
  cancel: string;
  breadcrumbs: string;
  filters: string;
  resetFilters: string;
  steps: string;
  stepCompleted: string;
  back: string;
  next: string;
}

/** Link of the app's router. Components render it for every `href`, so navigation stays client-side. */
export type UiLinkComponent = ComponentType<ComponentProps<'a'> & { href: string }>;

interface UiContextValue {
  labels: UiLabels;
  link: UiLinkComponent;
}

const UiContext = createContext<UiContextValue | null>(null);

function PlainLink({ children, ...props }: ComponentProps<'a'> & { href: string }) {
  return <a {...props}>{children}</a>;
}

export interface UiProviderProps {
  labels: UiLabels;
  /** Router link; without it, components render plain `<a>` elements. */
  link?: UiLinkComponent;
  children: ReactNode;
}

/** Root of every app using @nexus/ui: translated labels, router link and tooltip timing. */
export function UiProvider({ labels, link = PlainLink, children }: UiProviderProps) {
  return (
    <UiContext.Provider value={{ labels, link }}>
      <Tooltip.Provider delayDuration={400} skipDelayDuration={200}>
        {children}
      </Tooltip.Provider>
    </UiContext.Provider>
  );
}

function useUiContext(): UiContextValue {
  const value = useContext(UiContext);
  if (!value) throw new Error('Wrap the app in <UiProvider>.');
  return value;
}

export function useUiLabels(): UiLabels {
  return useUiContext().labels;
}

/** Link through the router the app passed to `UiProvider`. */
export function UiLink(props: ComponentProps<'a'> & { href: string }) {
  return createElement(useUiContext().link, props);
}
